/**
 * SOVRANOVA — session entry rules (Couples pre-beta remediation, Sep 2026)
 *
 * Pure, dependency-free decisions about what may be rendered or spoken when a
 * session opens, kept out of the React component so they can be tested with
 * plain `node --test` (frontend/tests/sessionEntry.test.mjs) and read in one
 * place.
 *
 * THE RULE: the mode (individual / couples) is chosen BEFORE the session
 * starts, and a couples session never receives a personalised opening. The
 * personalised greeting is built from the account holder's private memory and
 * last individual session; in a couples session a partner may already be
 * beside the device, so that greeting must not be generated, displayed or
 * spoken — not "made less specific", not generated. The server enforces the
 * same rule on GET /session/greet?mode=couples.
 */

export const MODES = Object.freeze(['individual', 'couples'])

// Identical to COUPLES_ENTRANCE in backend/routes/session.js — no name, no
// memory, no "last time".
export const COUPLES_ENTRANCE =
  'Welcome. Before we begin — is it just one of you here today, or both of you together?'

// The neutral default the individual screen shows while a greeting loads or
// if the greeting request fails (unchanged wording from SessionView).
export const INDIVIDUAL_DEFAULT_GREETING =
  "Welcome. I'm here with you. Before we begin — how have you been since we last spoke?"

/** Normalise any value to a known mode. Unknown → individual. */
export function normaliseMode(mode) {
  return MODES.includes(mode) ? mode : 'individual'
}

/** May a personalised (memory-based) greeting be fetched, shown or spoken? */
export function personalisedGreetingAllowed(mode) {
  return normaliseMode(mode) !== 'couples'
}

/**
 * What the entry screen does for a given mode.
 *   fetchPersonalisedGreeting — call GET /session/greet (individual only)
 *   greeting                  — the text to show/speak until (or instead of) a fetch
 *   speakGreeting             — whether the entrance line is voiced
 */
export function planEntry(mode) {
  const m = normaliseMode(mode)
  if (m === 'couples') {
    return { mode: m, fetchPersonalisedGreeting: false, greeting: COUPLES_ENTRANCE, speakGreeting: true }
  }
  return { mode: m, fetchPersonalisedGreeting: true, greeting: null, speakGreeting: true }
}

/**
 * The mode may change only BEFORE a session exists. Once a session has
 * started, switching either way is refused: individual → couples would put
 * memory-grounded replies already on screen in front of a partner;
 * couples → individual would continue a partner's words into a private turn.
 * Ending the session and starting another is the only path.
 */
export function canChangeMode({ sessionStarted }) {
  return !sessionStarted
}

/**
 * The "Complete & save to Reflections" (couples → individual promotion) action
 * is hidden for beta wave 1: the second tap is client-side only and the server
 * refuses the transfer for couples sessions unless the promotion flag is on.
 */
export function canOfferReflectionPromotion({ mode, promotionFlag }) {
  if (normaliseMode(mode) !== 'couples') return false
  return promotionFlag === true
}
